import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import './ProductUomPage.css';
import PageHeader from '../../../shared/PageHeader';
import BrowseNav, { useBrowseIds } from '../../../shared/BrowseNav';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import ControlFlags from '../../../shared/ControlFlags';
import FieldHelp from '../../../shared/FieldHelp';
import { AvailabilityPill } from '../../../shared/Pill';
import NumberInput from '../../../shared/NumberInput';
import { askConfirm } from '../../../shared/AppDialogs';

function AddUomForm({ availableUoms, controlTypes, onAdd }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [uomId, setUomId] = useState('');
  const [conversionRate, setConversionRate] = useState('');
  const [minOrderQty, setMinOrderQty] = useState('1');
  const [maxOrderQty, setMaxOrderQty] = useState('');
  const [qtyStep, setQtyStep] = useState('1');
  const [uomControlTypeId, setUomControlTypeId] = useState('');
  const [barcode, setBarcode] = useState('');
  const [saving, setSaving] = useState(false);

  const handleUomChange = (val) => {
    setUomId(val);
    const selected = availableUoms.find((u) => String(u.id) === String(val));
    setConversionRate(selected?.default_conversion_rate ?? '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uomId) return showError(null, 'Select a Unit of Measure.');
    if (!conversionRate) return showError(null, 'Conversion rate is required.');
    if (!uomControlTypeId) return showError(null, 'Select a UoM Control Type.');
    if (maxOrderQty && Number(maxOrderQty) < Number(minOrderQty || 1)) return showError(null, 'Max Order Qty cannot be less than Min Order Qty.');
    setSaving(true);
    try {
      await onAdd({
        uom_id: Number(uomId),
        conversion_rate: Number(conversionRate),
        min_order_qty: minOrderQty ? Number(minOrderQty) : 1,
        max_order_qty: maxOrderQty ? Number(maxOrderQty) : null,
        qty_step: qtyStep ? Number(qtyStep) : 1,
        uom_control_type_id: Number(uomControlTypeId),
        barcode: barcode.trim() || null,
      });
      setUomId('');
      setConversionRate('');
      setMinOrderQty('1');
      setMaxOrderQty('');
      setQtyStep('1');
      setUomControlTypeId('');
      setBarcode('');
      dismissError();
    } catch (err) {
      showError(err, 'Could not attach Unit of Measure');
    }
    setSaving(false);
  };

  const selectedType = controlTypes.find((t) => String(t.id) === String(uomControlTypeId));
  // Min/Max/Step Order Qty are a sales-ordering concept only (they feed
  // price-adjustment/quote resolution, which itself only ever considers
  // Can Sell rows) — dead controls on a Control Type that can't sell, so
  // disabled rather than left as a false affordance.
  const qtyFieldsDisabled = !!selectedType && !selectedType.can_sell;
  const qtyFieldsTitle = qtyFieldsDisabled ? "Only applies when this UoM's Control Type has Can Sell on" : undefined;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 12 }}>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Unit of Measure</label><br />
        <select className="styled-input" value={uomId} onChange={(e) => handleUomChange(e.target.value)} style={{ width: 160 }}>
          <option value="">(select)</option>
          {availableUoms.map((u) => (
            <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>UoM Control Type</label><br />
        <select className="styled-input" value={uomControlTypeId} onChange={(e) => setUomControlTypeId(e.target.value)} style={{ width: 170 }}>
          <option value="">(select)</option>
          {controlTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
          ))}
        </select>
        <div style={{ marginTop: 3 }}><ControlFlags type={selectedType} /></div>
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Conversion Rate</label><br />
        <NumberInput decimals={0} min="1" step="1" value={conversionRate} onChange={(e) => setConversionRate(e.target.value)} style={{ width: 90 }} />
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Min Order Qty</label><br />
        <NumberInput decimals={0} min="1" step="1" value={minOrderQty} disabled={qtyFieldsDisabled} title={qtyFieldsTitle} onChange={(e) => setMinOrderQty(e.target.value)} style={{ width: 80 }} />
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Max Order Qty</label><br />
        <NumberInput decimals={0} min="1" step="1" placeholder="none" value={maxOrderQty} disabled={qtyFieldsDisabled} title={qtyFieldsTitle} onChange={(e) => setMaxOrderQty(e.target.value)} style={{ width: 80 }} />
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Step Qty</label><br />
        <NumberInput decimals={0} min="1" step="1" value={qtyStep} disabled={qtyFieldsDisabled} title={qtyFieldsTitle} onChange={(e) => setQtyStep(e.target.value)} style={{ width: 70 }} />
      </div>
      <div>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Barcode</label><br />
        <input className="styled-input" type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} style={{ width: 130 }} />
      </div>
      <div>
        {/* Invisible label spacer, same height as every other field's
            label, so the button lines up with the selects/inputs beside
            it instead of sitting flush with the row's top (its own natural
            position once alignItems switched to flex-start). */}
        <label style={{ fontSize: 12, visibility: 'hidden' }}>Add</label><br />
        <button type="submit" className="primary-button" disabled={saving}>{saving ? 'Adding...' : '+ Add UoM'}</button>
      </div>
    </form>
  );
}

// UoM Conversions page (issue #95 + follow-up split): attaching Units of
// Measure to a product, Base UoM designation, and each row's conversion
// rate / Can Buy / Can Sell — the "set up this UoM" concerns. Deliberately
// separate from Reference Prices (ProductPricesPage.js): these are configured
// once and rarely change, while price schedules are an ongoing, growing
// timeline — cramming both into one table forced static attributes to sit
// alongside a variable-length, unrelated list.
function ProductUomPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const browseIds = useBrowseIds();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [allUoms, setAllUoms] = useState([]);
  const [controlTypes, setControlTypes] = useState([]);
  const [newBaseUomId, setNewBaseUomId] = useState('');
  const [baseUomSaving, setBaseUomSaving] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`/api/products/${id}/uoms`);
      setData(res.data.data);
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Could not load Units of Measure');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    axios.get('/api/uoms', { params: { status: 'active' } })
      .then((res) => setAllUoms(res.data.data || []))
      .catch(() => setAllUoms([]));
    axios.get('/api/uom-control-types', { params: { status: 'active' } })
      .then((res) => setControlTypes(res.data.data || []))
      .catch(() => setControlTypes([]));
    // eslint-disable-next-line
  }, [id]);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (loadError) return <div style={{ padding: 24, color: 'red' }}>{loadError}</div>;
  if (!data) return null;

  const { product } = data;
  const uoms = [...data.uoms].sort((a, b) => a.conversion_rate - b.conversion_rate);
  const baseLocked = uoms.length > 1;
  const attachedUomIds = new Set(uoms.map((r) => String(r.uom_id)));
  const availableToAttach = allUoms.filter((u) => !attachedUomIds.has(String(u.id)));
  const availableAsBase = allUoms.filter((u) => String(u.id) !== String(product.base_uom_id));

  const handleAddUom = async (payload) => {
    await axios.post(`/api/products/${id}/uoms`, payload);
    await fetchData();
  };

  const handleRemoveUom = async (rowId) => {
    if (!(await askConfirm('Remove this Unit of Measure from the product? Its price history will be deleted.', { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      await axios.delete(`/api/products/${id}/uoms/${rowId}`);
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not remove Unit of Measure');
    }
  };

  const handleUpdateRow = async (rowId, patch) => {
    try {
      await axios.put(`/api/products/${id}/uoms/${rowId}`, patch);
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not update Unit of Measure');
      await fetchData();
    }
  };

  const handleChangeBaseUom = async (e) => {
    e.preventDefault();
    if (!newBaseUomId) return showError(null, 'Select a Unit of Measure.');
    setBaseUomSaving(true);
    try {
      await axios.put(`/api/products/${id}/base-uom`, { uom_id: Number(newBaseUomId) });
      setNewBaseUomId('');
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not change Base Unit of Measure');
    }
    setBaseUomSaving(false);
  };

  return (
    <div className="product-uom-page" style={{ width: '100%', maxWidth: 1100, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 24px 32px' }}>
      <PageHeader
        crumbs={[
          { label: 'Products', onClick: () => navigate('/products') },
          { label: product.sku },
          { label: 'Product Units of Measure' },
        ]}
        title="Product Units of Measure"
        subtitle={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <strong>{product.sku} — {product.name}</strong>
            <AvailabilityPill badge={product.availability_badge} date={product.availability_date} color={product.availability_badge_color} showDate />
          </span>
        )}
        meta={(
          <>
            <button type="button" className="link-button" onClick={() => navigate(`/sales-prices?product_id=${id}`)}>Prices →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/products/${id}/media`, { state: browseIds ? { browseIds } : undefined })}>Product Media →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/products/${id}/weights`, { state: browseIds ? { browseIds } : undefined })}>Weights & Dimensions →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/products?edit=${id}`)}>Edit on Product</button>
          </>
        )}
      />

      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>Base Unit of Measure</h4>
        {baseLocked ? (
          <div style={{ fontSize: 14 }}>
            {uoms.find((r) => r.is_base)?.code} — {uoms.find((r) => r.is_base)?.name}
            <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Remove all additional Units of Measure below to change the Base UoM.</span>
          </div>
        ) : (
          <form onSubmit={handleChangeBaseUom} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="styled-input" value={newBaseUomId} onChange={(e) => setNewBaseUomId(e.target.value)} style={{ width: 200 }}>
              <option value="">(keep current — {uoms.find((r) => r.is_base)?.code})</option>
              {availableAsBase.map((u) => (
                <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
              ))}
            </select>
            <button type="submit" className="primary-button" disabled={baseUomSaving || !newBaseUomId}>{baseUomSaving ? 'Saving...' : 'Change Base UoM'}</button>
            <span style={{ fontSize: 12, color: '#888' }}>Set its UoM Control Type below once added.</span>
          </form>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Units of Measure
          <span style={{ marginLeft: 8, fontWeight: 400, textTransform: 'none' }}>{product.sku}</span>
        </h4>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead className="list-head">
            <tr>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Unit of Measure</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>UoM Control Type</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Conversion</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Min Qty</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Max Qty</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Step</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Barcode</th>
              <th style={{ padding: '10px 12px' }}></th>
            </tr>
          </thead>
          <tbody>
            {uoms.map((row) => (
              <tr key={row.id} className="list-row">
                <td style={{ padding: '6px 12px', fontWeight: 600 }}>
                  {row.code} — {row.name}
                  {row.is_base && <span style={{ display: 'block', fontSize: 11, color: '#1976d2', fontWeight: 700 }}>BASE UOM (G/L)</span>}
                </td>
                <td style={{ padding: '6px 12px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <select
                      className="styled-input"
                      value={row.uom_control_type_id || ''}
                      onChange={(e) => e.target.value && handleUpdateRow(row.id, { uom_control_type_id: Number(e.target.value) })}
                      style={{ width: 220 }}
                    >
                      {/* Placeholder only for a row still without a type —
                          every row needs one, so it's never pickable. */}
                      {!row.uom_control_type_id && <option value="" disabled>Select…</option>}
                      {controlTypes.map((t) => (
                        <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
                      ))}
                    </select>
                    {/* Fixed-width slot whether or not the icon renders, so
                        every row's control lands in the same spot instead of
                        shifting on rows that don't show it (same pattern as
                        the manual-price marker slot on the Ref Prices page).
                        A real FieldHelp popover, not a bare tooltip icon —
                        it needs to actually behave like every other "?" in
                        the app (open the relevant Manual article), not just
                        look like one. */}
                    <span style={{ width: 16, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {row.is_base && !row.can_sell && (
                        <FieldHelp
                          fieldKey="products.base-uom-not-sellable"
                          pageKey="products"
                          articleSlug="uom-control-types-locked"
                          articleTitle="UoM Control Types — Locked"
                        >
                          This UoM can't be sold (Can Sell is off) — its price is still required to compute the default price for other UoMs.
                        </FieldHelp>
                      )}
                    </span>
                  </span>
                  {/* Reads row.can_buy/sell/track directly (kept in
                      sync with the assigned type on every write) rather
                      than row.uom_control_type's own copies — one source,
                      no risk of the two drifting apart in this display. */}
                  <div style={{ marginTop: 3 }}><ControlFlags type={{ can_buy: row.can_buy, can_sell: row.can_sell, can_track: row.can_track }} /></div>
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                  {row.is_base ? (
                    <NumberInput decimals={0} value={1} disabled title="Fixed at 1 for base UoM" style={{ width: 70, textAlign: 'right' }} />
                  ) : (
                    <NumberInput
                      decimals={0}
                      min="1"
                      step="1"
                      defaultValue={row.conversion_rate}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val && val !== row.conversion_rate) handleUpdateRow(row.id, { conversion_rate: val });
                      }}
                      style={{ width: 70, textAlign: 'right' }}
                    />
                  )}
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                  <NumberInput
                    decimals={0}
                    min="1"
                    step="1"
                    defaultValue={row.min_order_qty}
                    disabled={!row.can_sell}
                    title={!row.can_sell ? "Only applies when this UoM's Control Type has Can Sell on" : undefined}
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (val && val !== row.min_order_qty) handleUpdateRow(row.id, { min_order_qty: val });
                    }}
                    style={{ width: 60, textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                  <NumberInput
                    decimals={0}
                    min="1"
                    step="1"
                    placeholder="none"
                    defaultValue={row.max_order_qty ?? ''}
                    disabled={!row.can_sell}
                    title={!row.can_sell ? "Only applies when this UoM's Control Type has Can Sell on" : undefined}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const val = raw === '' ? null : Number(raw);
                      if (val !== (row.max_order_qty ?? null)) handleUpdateRow(row.id, { max_order_qty: val });
                    }}
                    style={{ width: 60, textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                  <NumberInput
                    decimals={0}
                    min="1"
                    step="1"
                    defaultValue={row.qty_step}
                    disabled={!row.can_sell}
                    title={!row.can_sell ? "Only applies when this UoM's Control Type has Can Sell on" : undefined}
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (val && val !== row.qty_step) handleUpdateRow(row.id, { qty_step: val });
                    }}
                    style={{ width: 60, textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '6px 12px' }}>
                  <input
                    className="styled-input"
                    type="text"
                    defaultValue={row.barcode || ''}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val !== (row.barcode || '')) handleUpdateRow(row.id, { barcode: val || null });
                    }}
                    style={{ width: 120 }}
                  />
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                  {!row.is_base && (
                    <button type="button" className="action-button" style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }} onClick={() => handleRemoveUom(row.id)}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>Attach Unit of Measure</h4>
        <AddUomForm availableUoms={availableToAttach} controlTypes={controlTypes} onAdd={handleAddUom} />
      </div>

      <BrowseNav id={id} ids={browseIds} buildPath={(targetId) => `/products/${targetId}/uom`} noun="Products" />
    </div>
  );
}

export default ProductUomPage;
